<template>
  <v-container fluid id="daily-report-list" class="pa-0 pa-sm-3">
    <!-- Photos Carousel Section -->
    <v-dialog
      v-model="photosDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-toolbar dark color="primary" v-if="currentPhotos.length">
        <v-btn icon dark @click="photosDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-divider vertical class="ml-2 mr-4"></v-divider>
        <v-icon @click="carousel--" class="mr-3">
          chevron_left
        </v-icon>
        {{ carousel + 1 }}/{{ currentPhotos && currentPhotos.length }}
        <v-icon @click="carousel++" class="ml-3">
          chevron_right
        </v-icon>
        <v-toolbar-title class="ml-3">
          General Work
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-icon @click="downloadImage" class="mr-4">
          file_download
        </v-icon>
      </v-toolbar>
      <v-card>
        <v-card-text>
          <v-carousel
            v-model="carousel"
            hide-delimiter-background
            hide-delimiters
            height="auto"
            :show-arrows="false"
            class="pt-2"
          >
            <v-carousel-item
              v-for="(photo, index) in currentPhotos"
              :key="photo.id"
            >
              <!-- Only mount the full-res image for the current slide and its
                   immediate neighbors. Far slides stay empty, so we hold ~3
                   decoded 1400px images at a time instead of all of them — the
                   neighbors are preloaded so stepping left/right stays smooth. -->
              <v-img
                v-if="Math.abs(index - carousel) <= 1"
                :src="
                  photo.url.replace(
                    'https://firebasestorage.googleapis.com/',
                    'https://ik.imagekit.io/albademo/tr:w-1400/'
                  )
                "
                style="height:90vh"
                contain
                position="top center"
              ></v-img>
            </v-carousel-item>
          </v-carousel>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-row wrap class="justify-left">
      <v-col cols="12" xs="12" class="headline pb-0 pt-0">Daily Reports</v-col>
      <v-col cols="12" xs="12" md="8" class="pb-0 pt-2">
        <v-btn text outlined rounded small @click="goToToday" class="mr-1"
          >TODAY</v-btn
        >
        <v-btn icon small @click="prevDay"><v-icon>chevron_left</v-icon></v-btn>
        <v-dialog
          ref="dayDialog"
          v-model="datePickerDialog"
          :return-value.sync="day"
          persistent
          width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn text small rounded v-bind="attrs" v-on="on">{{
              day | moment("ddd MMM Do YYYY")
            }}</v-btn>
          </template>
          <v-date-picker v-model="day" scrollable color="primary">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="datePickerDialog = false"
              >Cancel</v-btn
            >
            <v-btn
              text
              color="primary"
              @click="
                $refs.dayDialog.save(day);
                goToDay(day);
              "
              >OK</v-btn
            >
          </v-date-picker>
        </v-dialog>
        <v-btn icon small @click="nextDay"
          ><v-icon>chevron_right</v-icon></v-btn
        >
      </v-col>
      <v-col cols="12" xs="12" md="8" class="pb-0 pt-4" v-if="!loading">
        <v-text-field
          v-model="search"
          outlined
          dense
          clearable
          label="Filter by Address"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="8" v-if="!loading">
        <v-card
          v-for="dailyReport in filteredDailyReports"
          :key="dailyReport.id"
          class="mb-4 elevation-3"
        >
          <v-card-title class="pb-0 pt-2">
            <span
              class="subtitle-1 black--text text--lighten-2 font-weight-bold"
              >{{ dailyReport.jobSite.street }}</span
            >
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              x-small
              outlined
              @click="openReport(dailyReport)"
            >
              View
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="mb-2">
              <strong>{{
                dailyReport.id.substring(0, 10) | moment("ddd MMM Do")
              }}</strong>
              by {{ dailyReport.completedBy.firstName }}
              {{ dailyReport.completedBy.lastName }}
              <v-chip 
                v-if="dailyReport.projectPhase"
                x-small 
                :color="getPhaseColor(dailyReport.projectPhase)"
                text-color="white"
                class="ml-2"
              >
                {{ dailyReport.projectPhase.charAt(0).toUpperCase() + dailyReport.projectPhase.slice(1) }}
              </v-chip>
            </div>
            <v-divider class="mb-1"></v-divider>
            <div
              v-for="(activity, index) in dailyReport.activities"
              :key="index"
              class="pa-0 mb-1"
            >
              <div v-if="activity.activityDescription.length">
                <div class="mb-1">
                  <strong>{{ activity.activityCategory }}</strong>
                </div>
                <p>
                  {{ activity.activityDescription.slice(0, 400)
                  }}<span v-if="activity.activityDescription.length > 400"
                    >...<a
                      style="text-decoration:underline;cursor:pointer"
                      @click="openReport(dailyReport)"
                      >VIEW FULL REPORT</a
                    ></span
                  >
                </p>
              </div>
            </div>
            <div class="pa-1 grey lighten-4">
              <v-slide-group multiple>
                <v-slide-item
                  v-for="(photo, ix) in dailyReport.photos.slice(0, 3)"
                  :key="ix"
                >
                  <v-img
                    lazy-src="https://placehold.jp/100x100.png"
                    @click="openPhotosDialog(dailyReport.photos, ix)"
                    :src="
                      photo.url.replace(
                        'https://firebasestorage.googleapis.com/',
                        'https://ik.imagekit.io/albademo/tr:w-100/'
                      )
                    "
                    height="100"
                    width="100"
                    max-height="100"
                    max-width="100"
                    style="display:inline-block;cursor: pointer"
                    class="mr-2"
                  />
                </v-slide-item>
                <!-- Only the first 3 thumbnails pre-load on the list to keep
                     image memory low on iPad; tapping "+N" opens the carousel
                     (windowed) where the rest can be cycled through. -->
                <v-slide-item v-if="dailyReport.photos.length > 3">
                  <div
                    @click="openPhotosDialog(dailyReport.photos, 3)"
                    class="d-flex align-center justify-center grey lighten-2 mr-2"
                    style="height:100px;width:100px;cursor:pointer;border-radius:4px"
                  >
                    <span class="subtitle-2 grey--text text--darken-2">
                      +{{ dailyReport.photos.length - 3 }}
                    </span>
                  </div>
                </v-slide-item>
              </v-slide-group>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        xs="12"
        sm="12"
        md="8"
        v-if="!loading && !dailyReports.length"
      >
        <v-card flat class="grey lighten-3 pa-6 text-center">
          <v-icon large color="grey" class="mb-2">event_busy</v-icon>
          <div class="subtitle-1 grey--text text--darken-2">
            No daily reports have been submitted yet for
            {{ day | moment("MM/DD/YYYY") }}.
          </div>
          <div class="body-2 grey--text">
            Use the arrows or date picker above to select a different day.
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="8" v-if="loading">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import fb from "@/main";
import { mapState } from "vuex";
import moment from "moment";
export default {
  name: "DailyReportsList",
  data() {
    return {
      datePickerDialog: false,
      search: "",
      photosDialog: false,
      currentPhotos: [],
      carousel: 0,
      dailyReports: [],
      loading: false,
      headers: [
        { text: "Submitted Date", value: "completedDate" },
        { text: "Report Date", value: "reportDate" },
        { text: "jobSite", value: "jobSite" },
        { text: "completed by", value: "completedBy" },
        { text: "", value: "button", align: "right" },
      ],
      snack: false,
      snackColor: "",
      snackText: "",
      backDay: null,
      lastFetchedOn: null,
      // One day at a time (default today). iPad memory can't handle many
      // reports' worth of photos at once; navigate days with the chevrons.
      day: moment().format("YYYY-MM-DD"),
    };
  },
  computed: {
    filteredDailyReports: function() {
      if (!this.search) return this.dailyReports;
      return this.dailyReports.filter((d) => {
        return (
          d.jobSite.address.toLowerCase().indexOf(this.search.toLowerCase()) >
          -1
        );
      });
    },
    ...mapState(["user"]),
  },
  async created() {
    try {
      this.getDailyReports();
    } catch (error) {
      alert("Error loading daily reports.");
      console.log(error);
      this.loading = false;
    }
  },
  // Runs every time the kept-alive list is re-shown (and once on first mount).
  // The list instance is cached by <keep-alive> so it normally never refetches.
  // To avoid showing yesterday's data after the day rolls over, refresh the
  // date window and refetch whenever the last load wasn't today. The
  // lastFetchedOn guard (set at the top of getDailyReports) makes first mount a
  // no-op here, so we never double-fetch alongside created().
  activated() {
    const today = moment().format("YYYY-MM-DD");
    if (this.lastFetchedOn !== today) {
      this.day = today;
      this.getDailyReports();
    }
  },
  methods: {
    async getDailyReports() {
      this.loading = true;
      // Stamp the day of this load so activated() knows the data is current and
      // skips its day-rollover refresh until the next calendar day.
      this.lastFetchedOn = moment().format("YYYY-MM-DD");
      const ref = await fb.db
        .collection("dailyReports")
        .where("date", ">=", this.day)
        .where("date", "<=", this.day)
        .where("complete", "==", true)
        .orderBy("date", "desc");
      const dailyReportRes = await ref.get();
      // Keep only the fields the list actually renders. The full report doc
      // averages ~53 KB, but ~90% of that is the base64 `signature` (plus
      // `weather`/`survey`) which this page never shows — dropping them here
      // keeps that mass out of memory and out of Vue's reactivity tracking.
      const dailyReports = [];
      dailyReportRes.forEach((j) => {
        const d = j.data();
        dailyReports.push({
          id: j.id,
          date: d.date,
          jobSite: d.jobSite,
          completedBy: d.completedBy,
          projectPhase: d.projectPhase,
        });
      });
      if (!dailyReports.length) {
        this.dailyReports = [];
        this.loading = false;
        return;
      }
      const photos = await this.getPhotos();
      const activities = await this.getActivities();
      // Merge photos/activities, then Object.freeze the whole result. Vue 2
      // skips reactivity on frozen objects, so this large read-only list costs
      // far less heap/CPU — we only ever replace the array wholesale on
      // refetch, never mutate a row in place.
      this.dailyReports = Object.freeze(
        dailyReports.map((d) =>
          Object.freeze({
            ...d,
            photos: photos.filter(
              (p) =>
                p.jobSiteId === d.jobSite.id &&
                p.date === d.date &&
                p.photoType === "General Work"
            ),
            activities: activities.filter(
              (p) => p.jobSiteId === d.jobSite.id && p.date === d.date
            ),
          })
        )
      );
      this.loading = false;
    },
    async getPhotos() {
      const ref = await fb.db
        .collection("dailyReportPhotos")
        .where("date", "==", this.day);
      const dailyReportPhotosRes = await ref.get();
      const dailyReportPhotos = [];
      dailyReportPhotosRes.forEach((j) => {
        const p = j.data();
        // Only the fields the list + carousel use; drops the embedded
        // `uploadedBy` user object carried on every photo doc.
        dailyReportPhotos.push({
          id: j.id,
          url: p.url,
          jobSiteId: p.jobSiteId,
          date: p.date,
          photoType: p.photoType,
        });
      });
      return dailyReportPhotos;
    },
    async getActivities() {
      const ref = await fb.db
        .collection("dailyReportActivities")
        .where("date", "==", this.day);
      const dailyReportActivitiesRes = await ref.get();
      const dailyReportActivities = [];
      dailyReportActivitiesRes.forEach((j) => {
        const a = j.data();
        // Only the fields the list renders / filters on.
        dailyReportActivities.push({
          jobSiteId: a.jobSiteId,
          date: a.date,
          activityCategory: a.activityCategory,
          activityDescription: a.activityDescription,
        });
      });
      return dailyReportActivities;
    },
    prevDay() {
      this.day = moment(this.day)
        .subtract(1, "d")
        .format("YYYY-MM-DD");
      this.getDailyReports();
    },
    nextDay() {
      this.day = moment(this.day)
        .add(1, "d")
        .format("YYYY-MM-DD");
      this.getDailyReports();
    },
    goToToday() {
      this.day = moment().format("YYYY-MM-DD");
      this.getDailyReports();
    },
    goToDay(date) {
      this.day = date;
      this.datePickerDialog = false;
      this.getDailyReports();
    },
    openReport(dailyReport) {
      // Navigate in-app rather than opening a new tab. New-tab links break in
      // installed PWAs (iOS hands them off to Safari), so we stay in the app and
      // rely on the back button on the summary page to return here.
      this.$router.push(
        `/daily-reports/${
          dailyReport.jobSite.id
        }/${dailyReport.id.substring(0, 10)}/summary`
      );
    },
    openPhotosDialog(photos, ix) {
      this.carousel = ix;
      this.currentPhotos = photos;
      this.photosDialog = true;
    },
    async downloadImage() {
      const photo = this.currentPhotos[this.carousel];
      const imageKitUrl = photo.url.replace(
        "https://firebasestorage.googleapis.com/",
        "https://ik.imagekit.io/albademo/"
      );
      try {
        const response = await fetch(imageKitUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `General_Work_${this.carousel + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log("ImageKit fetch failed, opening in new tab:", error.message);
        window.open(photo.url, "_blank");
      }
    },
    getPhaseColor(phase) {
      const colors = {
        'mobilization': 'orange',
        'in progress': 'green', 
        'demobilization': 'red'
      };
      return colors[phase] || 'grey';
    },
  },
};
</script>
